import React from "react";
import {
	Button,
	ChildrenProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
} from "@hex-insights/core";
import { EmptyView } from "../../../../Components/Empty";
import { Icon } from "../../../../Components/Icon";
import { AcademicTerm, Student, StudentNameFragment, useStudentEnrollmentsListQuery } from "../../../../Utilities";
import { AcademicTermFormat, ClassYearFormat, HomeRoomSectionFormat } from "../../../Format";
import { AcademicTermUtilities, ClassYearUtilities } from "../../../Utilities";
import { StudentUnenrollButton, StudentUnenrollPermissionRequired } from "../UnenrollButton";
import { StudentEnrollmentModalContentCourseSection } from "./CourseSection";
import { StudentEnrollmentFormController } from "./Form";
import { ControlledStudentEnrollmentStatusBadge, StudentEnrollmentStatusBadge } from "./StatusBadge";
import styles from "./styles.module.css";

export * from "./StatusBadge";

export type StudentEnrollmentStatusProps = {
	student: Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & StudentNameFragment;
};

export function StudentEnrollmentStatus({ student }: StudentEnrollmentStatusProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} style={{ padding: 0, border: "none" }}>
				<ControlledStudentEnrollmentStatusBadge studentID={student.id} />
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentEnrollmentModal student={student} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type StudentEnrollmentModalProps = {
	student: Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & StudentNameFragment;
} & Pick<ModalProps, "ifRef" | "onClose">;

function StudentEnrollmentModal({ student, ifRef, onClose }: StudentEnrollmentModalProps) {
	const {
		loading,
		academicTerms,
		selectedAcademicTerm,
		selectPreviousAcademicTerm,
		selectNextAcademicTerm,
		hasPreviousAcademicTerm,
		hasNextAcademicTerm,
	} = AcademicTermUtilities.useAcademicTermsNavigation();

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={styles["main-modal"]}>
			<Modal.Header style={{ position: "static" }}>
				<Conditional>
					<If condition={selectedAcademicTerm === null}>
						<Heading.H2 noMargin>Enrollment</Heading.H2>
					</If>
					<Else>
						{selectedAcademicTerm && (
							<Column>
								<Row justify="spaced-start" align="center" horizontalSpacing="1rem">
									<strong>Enrollment</strong>

									<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
										<Button
											variant="link"
											size="small"
											onClick={selectPreviousAcademicTerm}
											disabled={!hasPreviousAcademicTerm}
										>
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												<Icon.ArrowLeft size={14} />
												Previous
											</Row>
										</Button>

										<Button
											variant="link"
											size="small"
											onClick={selectNextAcademicTerm}
											disabled={!hasNextAcademicTerm}
										>
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												Next
												<Icon.ArrowRight size={14} />
											</Row>
										</Button>
									</Row>
								</Row>

								<Row justify="spaced-start" align="center" horizontalSpacing="1rem">
									<Heading.H2 noMargin>{AcademicTermFormat.name(selectedAcademicTerm)}</Heading.H2>

									<span
										style={{ fontSize: "0.875rem", fontStyle: "italic", color: "var(--neutral-colors--secondary)" }}
									>
										{AcademicTermFormat.Fields.startDate(selectedAcademicTerm.startDate)} -{" "}
										{AcademicTermFormat.Fields.endDate(selectedAcademicTerm.endDate)}
									</span>
								</Row>
							</Column>
						)}
					</Else>
				</Conditional>
			</Modal.Header>
			<Modal.Body style={{ display: "flex", flexDirection: "column", overflow: "scroll" }}>
				<Conditional>
					<If condition={academicTerms.length === 0 && loading}>
						<Column justify="center" align="center" style={{ flexGrow: 1 }}>
							<span>Loading Academic Terms...</span>
						</Column>
					</If>
					<If condition={academicTerms.length === 0}>
						<EmptyView
							IconComponent={Icon.Book}
							iconSize={48}
							primaryText="Enrollment"
							secondaryText="Please set up academic terms to enroll students."
						/>
					</If>
					<Else>
						{selectedAcademicTerm && (
							<StudentEnrollmentModalContent student={student} academicTerm={selectedAcademicTerm} />
						)}
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}

type StudentEnrollmentModalContentProps = {
	student: Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & StudentNameFragment;
	academicTerm: Pick<AcademicTerm, "id" | "name">;
};

function StudentEnrollmentModalContent({ student, academicTerm }: StudentEnrollmentModalContentProps) {
	const { loading: loadingEnrollments, data } = useStudentEnrollmentsListQuery({
		variables: {
			studentID: student.id,
			academicTermID: academicTerm.id,
		},
		fetchPolicy: "cache-and-network",
	});

	const academicTermEnrollment = data?.studentAcademicTermEnrollmentConnection.edges[0]?.node ?? null;
	const homeRoomSectionEnrollmentEdges = data?.studentHomeRoomSectionEnrollmentConnection.edges ?? [];
	const courseSectionEnrollmentEdges = data?.studentCourseSectionEnrollmentConnection.edges ?? [];

	const { loading: loadingClassYear, classYear } = ClassYearUtilities.useClassYear(student.yearsFromGraduation);

	if ((loadingEnrollments || loadingClassYear) && !data) {
		return (
			<Column justify="center" align="center" style={{ flexGrow: 1 }}>
				<span>Checking Enrollment Status...</span>
			</Column>
		);
	}

	const isEnrolledInTerm = academicTermEnrollment !== null;

	if (!isEnrolledInTerm) {
		return (
			<Column justify="spaced-start" style={{ flexGrow: 1 }}>
				<EmptyView
					IconComponent={Icon.Book}
					iconSize={48}
					primaryText="Enrollment"
					secondaryText="This student is not enrolled in this term."
				>
					<StudentEnrollmentFormController
						variant="link"
						student={student}
						academicTerm={academicTerm}
						academicTermEnrollment={academicTermEnrollment}
						homeRoomSectionEnrollmentEdges={homeRoomSectionEnrollmentEdges}
						courseSectionEnrollmentEdges={courseSectionEnrollmentEdges}
					>
						Click to Enroll
					</StudentEnrollmentFormController>
				</EmptyView>
			</Column>
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
			<Row justify="space-between" align="center" overflow="wrap">
				<StudentEnrollmentStatusBadge
					isEnrolled
					lateStartDate={academicTermEnrollment.lateStartDate}
					earlyEndDate={academicTermEnrollment.earlyEndDate}
				/>

				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<StudentEnrollmentFormController
						student={student}
						academicTerm={academicTerm}
						academicTermEnrollment={academicTermEnrollment}
						homeRoomSectionEnrollmentEdges={homeRoomSectionEnrollmentEdges}
						courseSectionEnrollmentEdges={courseSectionEnrollmentEdges}
					/>

					<StudentUnenrollPermissionRequired>
						<StudentUnenrollButton
							studentID={student.id}
							academicTermID={academicTerm.id}
							size="small"
							variant="custom"
							buttonClassName="button--danger-link"
							confirmButtonProps={{ variant: "danger", children: "Unenroll" }}
						>
							Unenroll
						</StudentUnenrollButton>
					</StudentUnenrollPermissionRequired>
				</Row>
			</Row>

			<If condition={!!academicTermEnrollment.lateStartDate || !!academicTermEnrollment.earlyEndDate}>
				<Row justify="spaced-start" horizontalSpacing="1.5rem">
					<If condition={!!academicTermEnrollment.lateStartDate}>
						<LabeledItem label="Late Start">
							{AcademicTermFormat.Fields.startDate(academicTermEnrollment.lateStartDate)}
						</LabeledItem>
					</If>
					<If condition={!!academicTermEnrollment.earlyEndDate}>
						<LabeledItem label="Early End">
							{AcademicTermFormat.Fields.endDate(academicTermEnrollment.earlyEndDate)}
						</LabeledItem>
					</If>
				</Row>
			</If>

			<Row justify="spaced-start" horizontalSpacing="1.5rem" style={{ marginBottom: "1rem" }}>
				{!!classYear && (
					<LabeledItem label="Class Year">
						<strong>{ClassYearFormat.name(classYear)}</strong>
					</LabeledItem>
				)}

				<If condition={homeRoomSectionEnrollmentEdges.length > 0}>
					<LabeledItem label="Home Room">
						{homeRoomSectionEnrollmentEdges.map((e) => (
							<strong key={e.node.id}>{HomeRoomSectionFormat.name(e.node.homeRoomSection)}</strong>
						))}
					</LabeledItem>
				</If>
			</Row>

			<StudentEnrollmentModalContentCourseSection
				enrollmentEdges={courseSectionEnrollmentEdges}
				style={{ flexGrow: 1 }}
			/>
		</Column>
	);
}

type LabeledItemProps = {
	label: string;
} & Partial<ChildrenProps>;

function LabeledItem({ label, children }: LabeledItemProps) {
	return (
		<Column>
			<span style={{ fontSize: "0.875rem", color: "var(--neutral-colors--secondary)" }}>{label}</span>
			{children}
		</Column>
	);
}
